import { createFileRoute, Link, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { listPublishedProducts } from "@/lib/admin/products.functions";
import { BRANDS, MODEL_MAP, type ModelId } from "@/lib/catalog";
import { ArrowRight, Star, Truck, Globe2, ShieldCheck, Wrench, Package, Clock, ChevronRight } from "lucide-react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FixhenLogo } from "@/components/FixhenLogo";
import { GearAnimation } from "@/components/GearAnimation";
import { BrandsStrip } from "@/components/BrandsStrip";
import { useInViewReveal } from "@/hooks/use-in-view-reveal";
import { HomeBlogStrip } from "@/components/blog/HomeBlogStrip";
import r129 from "@/assets/mercedes-r129.avif.asset.json";
import w124 from "@/assets/mercedes-w124.avif.asset.json";
import w201 from "@/assets/mercedes-w201.avif.asset.json";
import w208 from "@/assets/mercedes-w208.avif.asset.json";
import bmwE30 from "@/assets/bmw-e30.avif.asset.json";
import bmwE31 from "@/assets/bmw-e31.avif.asset.json";
import bmwE34 from "@/assets/bmw-e34.avif.asset.json";
import bmwE36 from "@/assets/bmw-e36.avif.asset.json";
import bmwE46 from "@/assets/bmw-e46.avif.asset.json";

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    // ensureQueryData caches through SSR so revisits within staleTime skip
    // the fetch waterfall entirely — much faster than a bare prefetchQuery.
    await context.queryClient.ensureQueryData({
      queryKey: ["shop", "published-products"],
      queryFn: () => listPublishedProducts(),
      staleTime: 60_000,
    });
  },
  head: () => ({
    meta: [
      { title: "FIXHEN — Classic Mercedes-Benz & BMW Parts, Shipped Worldwide" },
      { name: "description", content: "OEM and quality-matched parts for classic Mercedes-Benz and BMW. Worldwide delivery from FIXHEN." },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: "FIXHEN — Classic Mercedes-Benz & BMW Parts, Shipped Worldwide" },
      {
        property: "og:description",
        content: "OEM and quality-matched parts for the Mercedes R129, W124, W201, W208 and BMW E30, E31, E34, E36, E46. Free shipping over €99. Worldwide delivery.",
      },
      { property: "og:url", content: "https://fixhen.com/" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://fixhen.com/og/home.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://fixhen.com/og/home.jpg" },

    ],
    links: [{ rel: "canonical", href: "https://fixhen.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "FIXHEN",
              url: "/",
              logo: "/__l5e/assets-v1/8e19e2d7-5fc1-415c-bb35-1e49455e542f/fixhen-logo.png",
              sameAs: [],
            },
            {
              "@type": "WebSite",
              name: "FIXHEN",
              url: "/",
              potentialAction: {
                "@type": "SearchAction",
                target: "/?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "Store",
              name: "FIXHEN",
              description:
                "Classic Mercedes-Benz and BMW auto parts e-commerce. Free shipping over €99, worldwide delivery.",
              priceRange: "€€",
              areaServed: "Worldwide",
            },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

type ModelCardDef = { id: string; name: string; years: string; img: string };

const mercedesModels: ModelCardDef[] = [
  { id: "r129", name: "Mercedes R129", years: "1989 – 2001", img: r129.url },
  { id: "w124", name: "Mercedes W124", years: "1984 – 1997", img: w124.url },
  { id: "w201", name: "Mercedes W201", years: "1982 – 1993", img: w201.url },
  { id: "w208", name: "Mercedes W208", years: "1997 – 2003", img: w208.url },
];

const bmwModels: ModelCardDef[] = [
  { id: "e30", name: "BMW E30", years: "1982 – 1994", img: bmwE30.url },
  { id: "e31", name: "BMW E31", years: "1989 – 1999", img: bmwE31.url },
  { id: "e34", name: "BMW E34", years: "1988 – 1996", img: bmwE34.url },
  { id: "e36", name: "BMW E36", years: "1990 – 2000", img: bmwE36.url },
  { id: "e46", name: "BMW E46", years: "1997 – 2006", img: bmwE46.url },
];

/** Human-friendly count: 0, 1..9 exact, then 10+, 20+, 100+, 1k+… */
function formatCount(n: number): string {
  if (n <= 0) return "0";
  if (n < 10) return String(n);
  if (n < 100) return `${Math.floor(n / 10) * 10}+`;
  if (n < 1000) return `${Math.floor(n / 100) * 100}+`;
  return `${Math.floor(n / 1000)}k+`;
}

const reviews = [
  { name: "Lukas M.", car: "W124 300E owner · Germany", rating: 5, text: "Fitment was perfect on my 1991 300E. Box arrived in 4 days from FIXHEN, parts were factory-grade. I keep coming back." },
  { name: "Sofia R.", car: "R129 SL500 owner · Italy", rating: 5, text: "Found a soft-top cylinder no one else had in stock. Honest pricing, real photos, and they actually answered my fitment question in an hour." },
  { name: "James P.", car: "E46 M3 owner · UK", rating: 5, text: "Great curation for modern classics. The control arm came packed like a museum piece — that says everything about FIXHEN." },
];

function Index() {
  const { data: products = [] } = useSuspenseQuery({
    queryKey: ["shop", "published-products"],
    queryFn: () => listPublishedProducts(),
    staleTime: 60_000,
  });

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useInViewReveal(pathname);

  const stats = useMemo(() => {
    const byModel: Record<string, number> = {};
    const byBrand: Record<string, number> = {};
    for (const p of products as any[]) {
      byBrand[p.brand] = (byBrand[p.brand] ?? 0) + 1;
      for (const m of p.models ?? []) byModel[m] = (byModel[m] ?? 0) + 1;
    }
    return { total: products.length, byModel, byBrand };
  }, [products]);

  const featuredProducts = useMemo(() => (products as any[]).slice(0, 6), [products]);

  return (
    <div className="bg-background text-foreground">
      <AnnouncementBar />
      <SiteHeader />

      <main>
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-hero text-paper" style={{ contain: "layout style paint" }}>
        <div className="bg-grid absolute inset-0 opacity-[0.25]" aria-hidden />
        {/* Compact mechanism sits below the F card, in the clean dark corner
            of the hero — visible on its own without competing with headline
            or card content. */}
        <div className="pointer-events-none absolute bottom-6 right-6 hidden h-[240px] w-[380px] opacity-90 md:block lg:bottom-8 lg:right-10 lg:h-[280px] lg:w-[440px]">
          <GearAnimation className="h-full w-full" />
        </div>
        <div className="mx-auto grid max-w-7xl gap-5 px-4 pb-8 pt-4 sm:gap-8 sm:px-6 sm:pb-14 sm:pt-10 md:grid-cols-12 md:gap-10 md:pb-28 md:pt-20">
          <div className="md:col-span-7">
            <span className="animate-fade-up inline-flex items-center gap-1.5 rounded-full border border-sky-pop/30 bg-sky-pop/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-sky-pop sm:gap-2 sm:px-3 sm:py-1 sm:text-xs">
              <ShieldCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> Modern classic specialists
            </span>
            <h1 className="animate-fade-up mt-3 text-[28px] font-black leading-[1.08] tracking-tight sm:mt-5 sm:text-5xl md:text-6xl lg:text-7xl">
              Keep your <span className="text-gradient-brand">classic</span><br />
              running like new.
            </h1>
            <p className="animate-fade-up mt-3 max-w-xl text-sm text-paper/80 sm:mt-5 sm:text-lg">
              OEM and quality-matched parts for legendary Mercedes-Benz and BMW chassis — curated by enthusiasts, shipped to your door anywhere in the world.
            </p>

            <div className="animate-fade-up mt-5 flex flex-wrap gap-2 sm:mt-8 sm:gap-3" style={{ animationDelay: "0.15s" }}>
              <a href="#models" className="press inline-flex h-11 items-center gap-2 rounded-xl bg-sky-pop px-5 text-sm font-bold text-ink shadow-glow sm:h-12 sm:px-6">
                Shop by model <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#parts" className="press inline-flex h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 text-sm font-bold text-paper backdrop-blur transition-colors hover:bg-white/10 sm:h-12 sm:px-6">
                Featured parts
              </a>
            </div>

            <dl className="animate-fade-up mt-5 grid max-w-xl grid-cols-3 gap-2 sm:mt-10 sm:gap-4" style={{ animationDelay: "0.2s" }}>
              {[
                { k: formatCount(stats.total), v: stats.total === 1 ? "Part in stock" : "Parts in stock" },
                { k: "48h", v: "EU dispatch" },
                { k: "60+", v: "Countries shipped" },
              ].map((s) => (
                <div key={s.v} className="rounded-xl border border-white/10 bg-white/5 px-2.5 py-2 backdrop-blur sm:rounded-2xl sm:px-4 sm:py-3">
                  <dt className="text-lg font-black text-sky-pop sm:text-3xl">{s.k}</dt>
                  <dd className="mt-0.5 text-[10px] leading-tight text-paper/70 sm:text-xs">{s.v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="md:col-span-5">
            <div className="animate-fade-up shadow-elevated relative mx-auto max-w-sm rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur sm:rounded-3xl sm:p-8" style={{ animationDelay: "0.1s" }}>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-sky-pop sm:mb-4 sm:text-xs">Built with you in mind</p>
              <FixhenLogo animated className="mx-auto h-32 w-auto sm:h-48" />
              <div className="mt-3 grid grid-cols-2 gap-2 text-center text-[11px] sm:mt-6 sm:gap-3 sm:text-xs">
                <Badge icon={<Truck className="h-4 w-4" />}>Free over €99</Badge>
                <Badge icon={<Globe2 className="h-4 w-4" />}>Worldwide</Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px overflow-hidden md:grid-cols-4">
          {[
            { i: Truck, t: "Free shipping over €99", s: "Inside the EU" },
            { i: Globe2, t: "Worldwide delivery", s: "60+ countries" },
            { i: ShieldCheck, t: "2-year warranty", s: "On all OEM parts" },
            { i: Wrench, t: "Fitment guarantee", s: "Or your money back" },
          ].map((it, idx) => (
            <div
              key={it.t}
              className="reveal-up flex flex-col items-center gap-2 bg-background px-3 py-4 text-center sm:flex-row sm:items-center sm:gap-3 sm:px-5 sm:py-5 sm:text-left"
              style={{ ["--reveal-delay" as any]: `${idx * 70}ms` }}
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ink text-sky-pop">
                <it.i className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold leading-tight text-ink">{it.t}</p>
                <p className="mt-0.5 text-xs leading-tight text-muted-foreground">{it.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED MODELS */}
      <section id="models" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6 md:py-24 cv-auto">
        <div className="reveal-up">
          <SectionHead
            eyebrow="Top categories"
            title="Shop by featured model"
            sub="Our deepest catalogues — the chassis we live and breathe. Tap a model to see every part in stock."
          />
        </div>

        <h3 className="reveal-up mt-10 mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-royal">
          <span className="inline-block h-px w-8 bg-royal" /> Mercedes-Benz
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {mercedesModels.map((m, i) => (
            <ModelCard key={m.id} id={m.id} name={m.name} years={m.years} parts={stats.byModel[m.id] ?? 0} delay={i * 70}>
              <img
                src={m.img}
                alt={`${m.name} line drawing`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-contain"
              />
            </ModelCard>
          ))}
        </div>

        <h3 className="reveal-up mt-12 mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-royal">
          <span className="inline-block h-px w-8 bg-royal" /> BMW
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {bmwModels.map((m, i) => (
            <ModelCard key={m.id} id={m.id} name={m.name} years={m.years} parts={stats.byModel[m.id] ?? 0} delay={i * 70}>
              <img
                src={m.img}
                alt={`${m.name} line drawing`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-contain"
              />
            </ModelCard>
          ))}
        </div>
      </section>

      {/* BRANDS */}
      <div className="reveal-fade cv-auto"><BrandsStrip /></div>

      {/* FEATURED PARTS */}
      <section id="parts" className="bg-secondary/40 py-16 md:py-24 cv-auto">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="reveal-up">
            <SectionHead
              eyebrow="Featured parts"
              title="Fresh in the warehouse"
              sub="Hand-picked components from this week's restock. Real photos, honest fitment notes, ready to ship."
            />
          </div>
          {featuredProducts.length === 0 ? (
            <div className="mt-10 rounded-3xl border border-dashed border-border bg-background/60 p-12 text-center">
              <Package className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-4 text-lg font-bold text-ink">Fresh stock is on its way.</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Our warehouse is being restocked. Browse the full catalogue — fresh stock lands every week.
              </p>
              <Link
                to="/shop"
                className="press mt-6 inline-flex h-11 items-center gap-2 rounded-xl bg-ink px-5 text-sm font-bold text-paper"
              >
                Browse the shop <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {featuredProducts.map((p: any, idx: number) => (
                <Link
                  key={p.id}
                  to="/p/$slug"
                  params={{ slug: p.slug }}
                  className="reveal-up card-tilt group flex flex-col overflow-hidden rounded-2xl border border-border bg-background"
                  style={{ ["--reveal-delay" as any]: `${idx * 80}ms` }}
                >
                  <div className="relative aspect-square overflow-hidden bg-white">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.name}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center bg-white text-ink/15">
                        <Package className="h-16 w-16" />
                      </div>
                    )}
                    {p.inStock && (
                      <span className="absolute left-3 top-3 rounded-full bg-sky-pop px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink">
                        In stock
                      </span>
                    )}
                    <span className="absolute right-3 top-3 rounded-full bg-ink/80 px-2.5 py-1 text-[10px] font-mono text-paper backdrop-blur">
                      {p.sku}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-5">
                    {(() => {
                      const brandLabel =
                        BRANDS.find((b) => b.id === p.brand)?.label ??
                        (p.brand ? String(p.brand).charAt(0).toUpperCase() + String(p.brand).slice(1) : "");
                      const modelLabels = (p.models ?? []).map((m: string) => {
                        const info = MODEL_MAP[m as ModelId];
                        if (info) return info.label.toUpperCase();
                        const s = String(m);
                        return (s.includes("::") ? s.split("::")[1] : s).toUpperCase();
                      });
                      const shown = modelLabels.slice(0, 2);
                      const extra = modelLabels.length - shown.length;
                      return (
                        <p className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-royal">
                          {brandLabel ? <span>{brandLabel}</span> : null}
                          {brandLabel && shown.length > 0 ? (
                            <span className="text-muted-foreground">·</span>
                          ) : null}
                          {shown.length > 0 ? (
                            <span className="text-muted-foreground">{shown.join(" · ")}</span>
                          ) : null}
                          {extra > 0 ? (
                            <span
                              title={modelLabels.join(" · ")}
                              className="rounded-full border border-border bg-secondary/60 px-1.5 py-0.5 text-[10px] font-black text-muted-foreground"
                            >
                              +{extra} more
                            </span>
                          ) : null}
                        </p>
                      );

                    })()}
                    <h3 className="text-lg font-bold leading-snug text-ink">{p.name}</h3>
                    <div className="mt-auto flex items-end justify-between pt-3">
                      <div>
                        <p className="text-xs text-muted-foreground">From</p>
                        <p className="text-2xl font-black text-ink">€{Number(p.price ?? 0).toFixed(2)}</p>
                      </div>
                      <span className="press inline-flex h-11 items-center gap-1.5 rounded-xl bg-ink px-4 text-xs font-bold text-paper transition-colors group-hover:bg-royal">
                        View <ChevronRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 cv-auto">
        <div className="reveal-up"><SectionHead eyebrow="How it works" title="From catalogue to driveway" /></div>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            { i: Wrench, t: "Find your fitment", d: "Pick your model or paste any OEM number — we match it across every catalogue." },
            { i: Package, t: "We pack & ship", d: "Carefully packed in-house, dispatched within 48h from our EU warehouse." },
            { i: Clock, t: "Drive it tomorrow", d: "Free express shipping inside the EU on orders over €99. Worldwide tracked delivery." },
          ].map((s, i) => (
            <div
              key={s.t}
              className="reveal-in-right relative rounded-2xl border border-border bg-background p-6"
              style={{ ["--reveal-delay" as any]: `${i * 110}ms` }}
            >
              <span className="absolute -top-3 left-6 rounded-full bg-ink px-3 py-1 text-xs font-bold text-sky-pop">Step {i + 1}</span>
              <s.i className="h-7 w-7 text-royal" />
              <h3 className="mt-3 text-lg font-bold text-ink">{s.t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-ink py-16 text-paper md:py-24 cv-auto">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div className="reveal-up">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-sky-pop">Reviews</p>
              <h2 className="text-3xl font-black sm:text-4xl">Trusted by enthusiasts in 60+ countries</h2>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="flex" role="img" aria-label="4.9 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-sky-pop text-sky-pop" />
                ))}
              </div>
              <span className="font-semibold">4.9 / 5</span>
              <span className="text-paper/60">· 2,140+ reviews</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {reviews.map((r, idx) => (
              <figure
                key={r.name}
                className="reveal-scale card-tilt flex flex-col rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
                style={{ ["--reveal-delay" as any]: `${idx * 90}ms` }}
              >
                <div className="flex">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-sky-pop text-sky-pop" />
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-relaxed text-paper/85">"{r.text}"</blockquote>
                <figcaption className="mt-5 border-t border-white/10 pt-4">
                  <p className="text-sm font-bold">{r.name}</p>
                  <p className="text-xs text-paper/60">{r.car}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 cv-auto">
        <div className="reveal-scale relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-royal to-royal-deep p-8 text-paper sm:p-12">
          <div className="bg-grid absolute inset-0 opacity-20" aria-hidden />
          <div className="relative grid items-center gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-black sm:text-4xl">Can't find your part?</h2>
              <p className="mt-2 max-w-xl text-sm text-paper/80 sm:text-base">
                Send us your chassis number — our garage sources rare and discontinued parts daily. No bots, just gearheads.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row md:flex-col md:items-end">
              <a href="mailto:info@contact.fixhen.com" className="press inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-sky-pop px-6 text-sm font-bold text-ink">
                Request a part <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#models" className="press inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 text-sm font-bold text-paper">
                Browse catalogue
              </a>
            </div>
          </div>
        </div>
      </section>

      <HomeBlogStrip />
      </main>

      <SiteFooter />
    </div>
  );
}

function Badge({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-paper/90 sm:rounded-xl sm:py-2">
      <span className="text-sky-pop">{icon}</span>
      <span className="font-semibold">{children}</span>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <div className="max-w-2xl">
      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-royal">{eyebrow}</p>
      <h2 className="text-3xl font-black tracking-tight text-ink sm:text-4xl">{title}</h2>
      {sub && <p className="mt-3 text-base text-muted-foreground">{sub}</p>}
    </div>
  );
}

function ModelCard({ id, name, years, parts, children, delay = 0 }: { id: string; name: string; years: string; parts: number; children: React.ReactNode; delay?: number }) {
  const hasParts = parts > 0;
  return (
    <Link
      to="/shop"
      search={{ models: [id] } as any}
      className="reveal-up card-tilt group flex flex-col overflow-hidden rounded-2xl border border-border bg-background"
      style={{ ["--reveal-delay" as any]: `${delay}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-white">
        <div className="flex h-full w-full items-center justify-center">{children}</div>
        <span
          className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
            hasParts ? "bg-ink text-sky-pop" : "bg-secondary text-muted-foreground"
          }`}
        >
          {hasParts ? `${parts.toLocaleString()} ${parts === 1 ? "part" : "parts"}` : "Coming soon"}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-border p-4">
        <div className="min-w-0">
          <p className="truncate text-base font-bold text-ink">{name}</p>
          <p className="truncate text-xs text-muted-foreground">{years}</p>
        </div>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-secondary text-ink transition-colors group-hover:bg-ink group-hover:text-sky-pop">
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}
